// externals
import React from 'react';

// utils
import { normalizeVin } from '../../../utils/validation';
import { Tooltip } from '@makemydeal/ui-bricks/dist/cox';
import { formatYMMT } from './utils';

// components
import SearchInput from './SearchInput';
import TradePage from '../../../common/components/TradePage/TradePage';
import {
    SearchTitle,
    TabsContainer,
    Tab,
    SearchContainer,
    ToolTipContainer,
    ErrorMessageContainer,
    VehicleInfoPageWrapper
} from './VehicleInfoComponents';
import VehicleInfoDetails from '../VehicleInfoDetails/VehicleInfoDetails';

// consts/enums
import { TRADE_MANUAL_ENTRY } from '../../../utils';
import * as routes from '../../../utils/routes';

// interfaces
import { vehicleInfoUIProps } from './VehicleInfoInterfaces';
import { StepperRoute } from '../../../types';

// config
import config from '../../../store/mmd/config';
import { ITradeInSearchItem } from '../../../store';

export const vehicleInfoUI: React.FC<vehicleInfoUIProps> = ({
    amountOwed,
    isTradeInValid,
    isSearchingForVin,
    isSearchingForYMMT,
    isTrimLoading,
    isVinFetchRequired,
    externalTradeInConditions,
    tradeInStarted,
    tabSelected,
    setVehicle,
    renderDisclaimer,
    goToRoute,
    dispatchAnalytics,
    next,
    initializeOwnership,
    initializeSource,
    subaruEligibilityClickMultiEstimateOptions,
    subaruEligibilityClickUniqueEstimateOption,
    setTradeInIsSubaruGtpEligible,
    setTradeInSubaruGtpFlowLeaseError,
    resetTradeInSubaruGtpFlowError,
    uniqueEstimateOption,
    isAmdLienPayoffQuoteEnabled,
    isShowAppraisalValueScreenSalesView,
    stepperRoutes,
    isGtpEligible,
    vin,
    eligibilityFields,
    isSubaruGtpOn,
    isLease,
    nextStepRoute,
    prevStepRoute,
    nextStepTitle,
    showBackButton,
    showSkipButton,
    onNavigationButtonClick,
    onSkip,
    removeTradeIn,
    showZipCodeModalIfApplicable,
    cdlBackButtonClicked,
    cdlNextButtonClicked,
    cdlTradeInProgressStepperClicked,
    vinSearchResults,
    ymmtSearchResults,
    fetchVinSearchResults,
    fetchYmmtSearchResults,
    tradeinInfoTooltipPlacement,
    tradeIndex,
    multiTradeInState,
    urlPath,
    toastMessage
}) => {
    const [isVinActive, setIsVinActive] = React.useState(true);
    const [isYMMTActive, setIsYMMTActive] = React.useState(false);
    const [isNextDisabled, setIsNextDisabled] = React.useState(true);
    const [continueWasClicked, setContinueWasClicked] = React.useState(false);
    const [applyToAmountFinanced, setApplyToAmountFinanced] = React.useState(isLease ? true : !!amountOwed);
    const [duplicateVinError, setDuplicateVinError] = React.useState<string>('');

    const formatVin = (item: any, value: string) => `${item.yearId} ${item.makeName} ${item.modelName}`;

    React.useEffect(() => {
        if (isTradeInValid) {
            setIsVinActive(isSearchingForVin);
            setIsYMMTActive(isSearchingForYMMT);
            setIsNextDisabled(!isTradeInValid || isTrimLoading);
        }
    }, [isTradeInValid, isSearchingForVin, isSearchingForYMMT, isTrimLoading]);

    React.useEffect(() => {
        tradeInStarted();
        if (externalTradeInConditions.isPartialTrade) {
            if (externalTradeInConditions.hasAmountOwed) {
                initializeOwnership();
            }
            initializeSource();
            setIsVinActive(false);
            setIsYMMTActive(true);
        }
    }, []);

    React.useEffect(() => {
        if (!isVinFetchRequired) return;

        handleFetchVinSearchResults(vin);
    }, []);

    React.useEffect(() => {
        showZipCodeModalIfApplicable();
    }, []);

    const handleFetchVinSearchResults = (vinValue: string): void => {
        // Clear any previous error when user starts typing
        if (duplicateVinError) {
            setDuplicateVinError('');
        }

        // Check for duplicate VIN in multi-trade-ins before fetching
        if (multiTradeInState && multiTradeInState.tradeIns.length > 1 && vinValue) {
            const isDuplicate = multiTradeInState.tradeIns.some((tradeInItem, index) => {
                // Skip the current trade-in being edited
                if (index === tradeIndex) return false;

                const existingVin = tradeInItem.tradeInComponent?.vehicle?.vin;
                return existingVin && existingVin.toUpperCase() === vinValue.toUpperCase();
            });

            if (isDuplicate) {
                setDuplicateVinError('This VIN is already in use. Enter a unique VIN.');
                setIsNextDisabled(true);
                return;
            }
        }

        // Clear any previous error and proceed with fetch
        setDuplicateVinError('');
        fetchVinSearchResults(vinValue);
    };

    const handleVinSelect = (trade: ITradeInSearchItem, vin: string | false): void => {
        const vinValue = vin ? vin : '';

        // Clear any previous error
        setDuplicateVinError('');
        trade.vin = vinValue;
        setVehicle(trade);
        setIsNextDisabled(false);
    };

    const setVinActive = () => {
        tabSelected('vin');
        setIsVinActive(true);
        setIsYMMTActive(false);
        setContinueWasClicked(false);
    };

    const setYMMTActive = () => {
        tabSelected('ymm');
        setIsVinActive(false);
        setIsYMMTActive(true);
        setContinueWasClicked(false);
    };

    const getNextButtonText = () => {
        const isSubaruUniqueOptionFlow = isSubaruGtpFlowAvailable && uniqueEstimateOption.hasUniqueOption;
        const isSubaruUniqueLoanBalanceFlow = isAmdLienPayoffQuoteEnabled && isSubaruUniqueOptionFlow;

        if (isSubaruUniqueLoanBalanceFlow) {
            return 'Continue to Loan Balance';
        }
        if (isSubaruUniqueOptionFlow) {
            return 'Continue to Summary';
        }
        if (uniqueEstimateOption.hasUniqueOption) {
            return 'Continue to Details';
        }
        return 'Continue to Estimate Method';
    };

    const processStepperRoutesSubaruGTP = (stepperRoutes: StepperRoute[]) => {
        if (isSubaruGtpOn && isVinActive && isGtpEligible && uniqueEstimateOption.hasUniqueOption) {
            return stepperRoutes.filter(
                (stepRoute) => stepRoute.route !== routes.TRADE_IN_DECISION && stepRoute.route !== routes.TRADE_VEHICLE_CONDITION
            );
        }
        return stepperRoutes;
    };

    const renderTabs = () => {
        return (
            <>
                <Tab className={`tab-vin common-header ${isVinActive ? 'active' : ''}`} onClick={setVinActive}>
                    VIN
                </Tab>
                <Tab className={`tab-ymmt common-header ${isYMMTActive ? 'active' : ''}`} onClick={setYMMTActive}>
                    Enter Manually
                </Tab>
            </>
        );
    };

    const TooltipWrapper = () => {
        const ToolTipContent = () => (
            <>
                <label>Where's my VIN?</label>
                <p>A Vehicle Identification Number (VIN) is unique to your car. It's 17 characters long (digits and letters)</p>
                <br />
                <label>You can typically find your VIN in a few places:</label>
                <ol>
                    <li>Stamped on the dashboard near the windshield, on the driver's side of the vehicle.</li>
                    <li>On a plate or sticker on the driver's side door jamb.</li>
                    <li>On your insurance card and insurance policy.</li>
                    <li>On your vehicle title and registration.</li>
                </ol>
            </>
        );
        const TooltipWrapper = ({ content, placement }) => <Tooltip placement={placement}>{content}</Tooltip>;

        return (
            <ToolTipContainer>
                <TooltipWrapper content={<ToolTipContent />} placement={tradeinInfoTooltipPlacement} />
            </ToolTipContainer>
        );
    };

    const renderVinSearch = () => {
        if (!isVinActive) return <></>;

        return (
            <>
                <SearchTitle>
                    <label htmlFor="trade-search-vin">VIN</label>
                    <TooltipWrapper />
                </SearchTitle>
                <SearchInput
                    inputId="trade-search-vin"
                    name="vin"
                    placeholder="e.g. RTG567908THG"
                    fetchCallback={handleFetchVinSearchResults}
                    normalizeCallback={normalizeVin}
                    formatResult={formatVin}
                    selectCallback={(item, vin) => handleVinSelect(item, vin)}
                    searchResults={vinSearchResults}
                    isSearching={isSearchingForVin}
                    errorMessage={duplicateVinError}
                />
                {duplicateVinError && <ErrorMessageContainer>{duplicateVinError}</ErrorMessageContainer>}
                <VehicleInfoDetails
                    nextDisabled={setIsNextDisabled}
                    setApplyToAmountFinanced={setApplyToAmountFinanced}
                    isApplyToAmountFinanced={applyToAmountFinanced}
                    isVinActive={isVinActive}
                    continueWasClicked={continueWasClicked}
                    tradeIndex={tradeIndex}
                />
            </>
        );
    };

    const handleYMMTSelect = (item: any) => {
        setVehicle(item);
        setIsNextDisabled(false);
    };

    const renderYMMTSearch = () => {
        if (!isYMMTActive) return <></>;
        return (
            <>
                <SearchTitle>
                    <label htmlFor="trade-search-ymmt">Search</label>
                </SearchTitle>
                <SearchInput
                    inputId="trade-search-ymmt"
                    name="ymmt"
                    placeholder="e.g. 2015 Honda Pilot"
                    fetchCallback={fetchYmmtSearchResults}
                    formatResult={formatYMMT}
                    selectCallback={handleYMMTSelect}
                    searchResults={ymmtSearchResults}
                    isSearching={isSearchingForYMMT}
                />
                <hr />
                <VehicleInfoDetails
                    nextDisabled={setIsNextDisabled}
                    setApplyToAmountFinanced={setApplyToAmountFinanced}
                    isApplyToAmountFinanced={applyToAmountFinanced || externalTradeInConditions.hasAmountOwed}
                    continueWasClicked={continueWasClicked}
                />
            </>
        );
    };

    const isSubaruGtpFlowAvailable = isVinActive && isGtpEligible && isSubaruGtpOn;

    const handleNext = () => {
        if (!isTradeInValid) {
            setContinueWasClicked(true);
            return;
        }

        resetTradeInSubaruGtpFlowError();
        setTradeInIsSubaruGtpEligible(false);
        cdlNextButtonClicked(getNextButtonText(), isVinActive ? 'VIN' : 'Enter Manually');

        if (isSubaruGtpFlowAvailable) {
            if (isLease) {
                setTradeInSubaruGtpFlowLeaseError();
            } else if (uniqueEstimateOption.hasUniqueOption) {
                subaruEligibilityClickUniqueEstimateOption(
                    eligibilityFields,
                    uniqueEstimateOption,
                    isShowAppraisalValueScreenSalesView
                );
                return;
            } else if (uniqueEstimateOption.hasUniqueOption === false) {
                subaruEligibilityClickMultiEstimateOptions(eligibilityFields);
                return;
            }
        }

        next(
            externalTradeInConditions.isPartialTrade ? TRADE_MANUAL_ENTRY : '',
            uniqueEstimateOption,
            isShowAppraisalValueScreenSalesView
        );
    };

    const onBackButtonClick = () => {
        cdlBackButtonClicked();
        if (prevStepRoute) {
            onNavigationButtonClick(prevStepRoute);
        }
    };

    const onRemoveButtonClick = () => {
        // Remove the trade-in at the specific index (or current trade if index not provided)
        removeTradeIn(tradeIndex);
    };

    const onSkipButtonClick = () => {
        if (nextStepRoute) {
            onSkip(nextStepRoute);
        }
    };

    const PageSubTitle =
        "Let's start with the basics. You can look up your current vehicle or enter the details manually. All input fields are required unless marked as optional.";

    return (
        <VehicleInfoPageWrapper>
            <TradePage
                headerOnTop
                showCustomTitle
                pageClass="vehicle-info-page"
                pageTitle="Tell Us About Your Vehicle"
                pageSubTitle={PageSubTitle}
                renderDisclaimer={renderDisclaimer}
                goToRoute={goToRoute}
                dispatchAnalytics={dispatchAnalytics}
                urlPath={urlPath}
                footerProps={{
                    onActionButtonClick: handleNext,
                    // Show "Cancel Trade-In" only for 2nd trade onwards (tradeIndex > 0)
                    // For first trade (tradeIndex === 0 or undefined), show regular back button
                    showBackButton: tradeIndex > 0 ? showSkipButton : showBackButton,
                    backButtonText: tradeIndex > 0 ? 'Cancel Trade-In' : '', // Custom text only for 2nd+ trades
                    onBackButtonClick: tradeIndex > 0 ? onRemoveButtonClick : onBackButtonClick, // Remove for 2nd+, navigate back for 1st
                    backButtonAriaLabel:
                        tradeIndex > 0 ? 'Cancel Trade-In and remove this trade-in' : 'Back to the previous step',
                    showSkipButton,
                    skipButtonText: 'Skip',
                    skipButtonAriaLabel: nextStepTitle ? `Skip to ${nextStepTitle}` : undefined,
                    onSkipButtonClick: onSkipButtonClick,
                    buttonText: getNextButtonText(),
                    disableActionButton: isNextDisabled,
                    fullWidthLayout: true
                }}
                stepperRoutes={processStepperRoutesSubaruGTP(stepperRoutes)}
                showTradeErrorMessage={false}
                staticAssetsConfig={config.staticImages}
                stepLabelVisible={true}
                isStepperClickable={true}
                cdlPageProgressStepperClicked={cdlTradeInProgressStepperClicked}
                toastMessage={toastMessage}
            >
                <div className="search-tabs-container">
                    <TabsContainer className="search-tabs">{renderTabs()}</TabsContainer>
                    <SearchContainer className="search-body">
                        {renderVinSearch()}
                        {renderYMMTSearch()}
                    </SearchContainer>
                </div>
            </TradePage>
        </VehicleInfoPageWrapper>
    );
};

export default vehicleInfoUI;
